import { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { setCreatingNode, setNodeFormInitialValues } from 'Actions/explore'
import { updateGraphNodes } from 'Actions/graph'
import { Row, Col, Form, Input, Layout, Button, Checkbox, InputNumber, message} from 'antd'
import { getNamespaces, getNamespaceValue, getAttributeKeys, getAttributeKeyLabel, getMemberKey } from 'Services/WeaverUtils'
import { NodePicker, RelationPicker } from 'Components'
import ColorService from 'Services/ColorService'
import { loadGraphDataFromNodes } from 'Services/GraphUtils'
import ShortcutManager from 'Services/ShortcutManager'
import Feather from '@sysunite/react-feather'
import WeaverService from 'Services/WeaverService'

CreateNodeView = ({ updateGraphNodes, setCreatingNode, memberKeys, setNodeFormInitialValues, nodeFormInitialValues }) ->

  [nodeAmount, setNodeAmount] = useState(1)
  [saving, setSaving] = useState(false)
  [keepCreating, setKeepCreating] = useState(false)
  [convertNamespaces, setConvertNamespaces] = useState(true)

  realKey = getAttributeKeys()[0] or 'name'
  attributeKey = getAttributeKeyLabel(realKey) or 'name'
  initialFormData = {
    nodeFormInitialValues...,
    nameKey: realKey
  }

  formRef = useRef()
  fieldRef = useRef()

  useEffect( ->
    fieldRef.current?.focus()
    formRef.current?.setFieldsValue(initialFormData)
    () -> setNodeFormInitialValues(undefined)
  , [])

  showErrorMessage = (msg) ->
    errMsg = msg.split("ERROR: ")[1]
    if errMsg.includes('already exists') or errMsg.includes('more than one row returned by a subquery used as an expression')
      message.error("A node with this identifier already exists")
    else
      message.error("Error occured saving node")

  shouldUpdateTypes = (rels) ->
    if rels? and rels.length > 0
      rels.map((rel) ->
        rel.keys.find((key) ->
          memberKeys.includes(key)
        )
      )
    else
      false

  createNodeBatch = (values) ->
    setSaving(true)
    nodes = (createNode(values) for n in [0 ... values.nodeAmount])

    try
      await Weaver.Node.batchSave(nodes, { commitMessage: "Edit #{ values.nodeAmount } nodes from the edit node view." })
      ids = nodes.map(({ nodeId }) -> nodeId)

      createdNodes = await new Weaver.Query()
        .restrict(ids)
        .inGraph(nodes[0].graph)
        .selectOut('*')
        .find()

      setSaving(false)

      if not keepCreating
        close()
      else
        formRef.current?.resetFields([['nameValue']])
        fieldRef.current?.focus()

      await WeaverService.updateTypesMap() if shouldUpdateTypes(values.relations)
      updateGraphNodes(await loadGraphDataFromNodes([ createdNodes... ]))
    catch e
      showErrorMessage(e.message)
      setSaving(false)

  createNode = (values) ->
    values.id = undefined if _.trim(values.id) is ""
    values.graph = undefined if _.trim(values.graph) is ""

    if values.id?
      id = getNamespaceValue(values.id)
    else
      id = undefined

    if nodeAmount > 1
      node = new Weaver.Node(undefined, values.graph)
    else
      node = new Weaver.Node(id, values.graph)

    key = values.nameKey
    value = values.nameValue

    if value? and _.trim(value) isnt ''
      node.set(getNamespaceValue(key) or realKey, value)

    for key, values of values.relations
      { keys, targets } = values
      if keys? and targets?
        targets.map((uid) ->
          [id, graph] = uid.split('@')
          keys.map((key) ->
            node.relation(key).add(Weaver.Node.getFromGraph(id, graph))
          )
        )
    node

  close = ->
    setCreatingNode(false)

  onKeyDown = (e) ->
    if e.keyCode is 27
      ShortcutManager.eventsMap['esc']?.fn()

  <Layout style={{ overflow: 'auto', borderRadius: '5px', background: 'var(--background)', padding: '16px' }}>
    <Row align="middle">
      <Col style={{ flex: 1 }}>
        <h1>Create node</h1>
      </Col>
      <Col>
        <Button data-testid="close" onClick={close} size="large" type="link"><Feather type="x"/></Button>
      </Col>
    </Row>
    <Form
      ref={formRef}
      onFinish={createNodeBatch}
    >
      <Form.Item name="id">
        <Input
          data-testid="id"
          placeholder="Identifier (optional)"
          disabled={ if nodeAmount > 1 then true else false }
        />
      </Form.Item>
      <Form.Item name="graph">
        <Input data-testid="graph" placeholder="Graph (optional)"/>
      </Form.Item>
      <Form.Item>
        <Input.Group compact style={{ display: 'flex' }}>
          <Form.Item name='nameKey'>
            <Input data-testid="name-key"/>
          </Form.Item>
          <Form.Item style={{ flex: 1 }} name='nameValue'>
            <Input data-testid="name-value" ref={fieldRef} onKeyDown={onKeyDown} />
          </Form.Item>
        </Input.Group>
      </Form.Item>
      <Form.List name="relations">
        {(fields, { add, remove }) ->
          <div>
            {
              fields.map((field, index) ->
                <Form.Item key={field.key}>
                  <Row gutter={8} type="flex">
                    <Col style={{ flex: 1 }}>
                      <Form.Item name={[field.name, 'keys']} rules={[{ required: true, message: 'A relation key is required.' }]}>
                        <RelationPicker />
                      </Form.Item>
                    </Col>
                    <Col style={{ flex: 1 }}>
                      <Form.Item name={[field.name, 'targets']} rules={[{ required: true, message: 'A relation key is required.' }]}>
                        <NodePicker
                          mode="multiple"
                          query={
                            new Weaver.Query()
                              .selectIn(getMemberKey())
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col>
                      <Button data-testid="remove-relation" type="link" onClick={-> remove(field.name)}><Feather type="x" /></Button>
                    </Col>
                  </Row>
                </Form.Item>
              )
            }
            <Form.Item>
              <Button data-testid="add-relation" onClick={-> add()} type="dashed" block>
                <Feather type="plus" /> Add relation
              </Button>
            </Form.Item>
          </div>
        }
      </Form.List>
      <Form.Item
        name="nodeAmount"
        initialValue={1}
        label="Number of Nodes"
        labelAlign="left"
        style={{float: 'left'}}
      >
        <InputNumber
          min={1}
          onChange={setNodeAmount}
        />
      </Form.Item>
      <Form.Item style={{ float: 'right' }}>
        <Checkbox data-testid="keep-creating" checked={keepCreating} onChange={({ target: { checked }}) -> setKeepCreating(checked)}>Keep creating nodes</Checkbox>
        <Checkbox data-testid="auto-convert-namespace" checked={convertNamespaces} onChange={({ target: { checked }}) -> setConvertNamespaces(checked)}>Convert namespace to full URI</Checkbox>
        <Button data-testid="save" htmlType="submit" loading={saving} type="primary">Save</Button>
      </Form.Item>
    </Form>
  </Layout>

mapStateToProps = ({ explore }) -> {
  memberKeys: explore.memberKeys
  nodeFormInitialValues: explore.nodeFormInitialValues
}

export default connect(
  mapStateToProps,
  { updateGraphNodes, setCreatingNode, setNodeFormInitialValues }
)(CreateNodeView)
